import { useCallback, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SeriesKey } from '../../api/types.js';
import { color, font, fontSize, tint } from '../../theme/tokens.js';
/*
 * `breakdownRows` only — the chart lays the rows out itself since 2026-08-29.
 *
 * `<BreakdownPanel>` is one series' worth of panel at table scale, and the bubble now carries one
 * section PER series. Four of those at 11.5px with 10px/13px padding runs ~350px tall against a
 * 240px plot. What must not fork is the *semantics* — External derived rather than fetched, and
 * JOBS carrying no Source or Targeting axis — and those live in `breakdownRows`, which is shared.
 * Layout is the part that legitimately differs; the rules are the part that may not.
 */
import { ProvenanceFootnote, breakdownRows } from '../Breakdown/Breakdown.js';
import { provenanceFor, sourceFace } from '../Breakdown/provenance.js';
import { formatValue, formatValueExact } from '../MetricCell/MetricCell.js';
import { ChartTable, type ChartTableSeries } from './ChartTable.js';
import {
  SERIES_FULL,
  SERIES_LABEL,
  ROW_ORDER,
  SERIES_ORDER,
  bucketChange,
  changeFace,
  formatBucketWindow,
  type ChartData,
  type ChartPoint,
  type ChartRowKey,
} from './chartData.js';
import { ChartViewToggle, type ChartView } from './ViewToggle.js';

/*
 * The wire shape and the series maps live in `chartData.ts` (2026-09-09), because the table view
 * reads the identical response and must name every series exactly as the legend does. Re-exported
 * here so `import { type ChartData } from './LineChart.js'` keeps working — every page and test
 * in the product spells it that way.
 */
export type { ChartData, ChartPoint } from './chartData.js';

/**
 * The 12-point, 4-series chart — hand-rolled inline SVG (FR-024, FR-025, FR-027, R-08).
 *
 * **No charting library.** The blueprint's chart is `<path>` + `<circle>` with *zero*
 * `<text>` nodes, matching PRD §6.2's "no Y-axis labels or grid". Every library ships axes,
 * gridlines, legends, and margins that would have to be suppressed one prop at a time, each
 * upgrade risking silent visual drift against a blueprint Principle I makes non-negotiable.
 * What is actually needed is a linear scale, which is the two lines below.
 *
 * Each series is scaled **independently**. TOA runs in the hundreds while JOBS runs in the
 * tens; on a shared axis the three smaller series would flatten into the baseline and the
 * chart would answer nothing. With no Y-axis labels the chart is a shape comparison, so
 * per-series scaling is the honest reading of it — and the tooltip carries the real figures.
 *
 * `kind: 'stock'` on TOA is served by the API and shown in the tooltip: TOA is an as-of count
 * at each bucket end, not a sum over the bucket (FR-025).
 */

/**
 * The chart draws in **real pixels**, not in the blueprint's fixed 740×170 user space.
 *
 * The blueprint ships `viewBox="0 0 740 170"` on a `width:100%;height:210px` element, which
 * under the default `preserveAspectRatio` scales by `min(W/740, 210/170)`. Past a container
 * width of 914px the height becomes the binding constraint, the drawing stops growing, and the
 * remainder turns into empty margin either side: on a 1440px screen the 12 points spanned 78%
 * of the card, on a 1920px screen 54%, and the line never got longer than 860px however wide
 * the monitor. It read as a chart that had failed to fill its own panel.
 *
 * Measuring the container and setting `viewBox` to the measured width keeps the scale at
 * exactly 1:1, so there is no letterboxing at any width — and, unlike
 * `preserveAspectRatio="none"`, circles stay circular and `STROKE_WIDTH` stays `STROKE_WIDTH`.
 *
 * `PAD_X` is gone rather than reduced: the first and last points sit **on** the edges, which is
 * what "12 points across this card" should mean. `overflow: visible` on the `<svg>` is what pays
 * for it — a swollen end dot plus its halo would otherwise be clipped in half. The ~5px spill
 * lands inside the panel's own 20px padding.
 *
 * `CHART_HEIGHT` is **240px, not the blueprint's rendered 210** (owner call 2026-08-28). Twelve
 * points across ~1200px of card at 210px is a shallow ribbon, and real month-to-month movement
 * flattens into the same gentle undulation as noise. 240 is the smallest height that gives the
 * shapes room to differ. `PAD_Y` carries the blueprint's 14/170 inset over to it
 * (14 ÷ 170 × 240 ≈ 20), so the proportions are still the blueprint's — the height is the
 * deliberate deviation, logged here rather than left to be discovered.
 */
const FALLBACK_WIDTH = 740;
const CHART_HEIGHT = 240;
const PAD_Y = 20;

/**
 * The swing the **full plot height stands for** (owner request 2026-09-02, widened 2026-09-08).
 *
 * Each series is scaled to its own values — there is no Y axis and no shared domain — so before
 * this, the domain was exactly `[min, max]` and every series filled the plot from inset to inset
 * whatever its numbers did. That is right for a series that moves, and badly wrong for a stock
 * that does not: TOA sits around 480 all quarter, so a 470 → 490 wobble — **4%**, and noise —
 * was stretched over 200px and drawn as a mountain range. With nothing on screen to give the
 * height a scale, the reader has no way to tell that cliff from a real collapse.
 *
 * So the domain is floored at a **proportion of the series' own level** — and at `1` that floor
 * stops being a floor and becomes a statable rule: **the plot height is one whole level, so
 * vertical distance reads directly as percent-of-level**. A series that swings 10% of its level
 * draws 10% of the height, one that swings 40% draws 40%, and one that swings more than 100% —
 * which only a flow does — still fills the plot exactly as it did before. Nothing is capped,
 * clipped or averaged; the eye simply stops magnifying small movement into a cliff.
 *
 * **It began at 20%, and 20% was still magnifying** (widened 2026-09-08, on the same complaint
 * the band was introduced for — the lines spiked where the figures barely moved). At `0.2` the
 * height meant a *minimum* 20% swing, so everything under that was drawn 5× its size: monthly
 * TOA over all regions runs 453 → 505 — **10.9%** of its level — and drew at 54% of the plot, so
 * an ordinary +9.1% March read as half a chart. At `1` that same series draws at 11% of the
 * height, which is what it is. Region-level TOA swings 17% (R5) to 44% (SPOT) and now draws at
 * 17–44% of the height rather than 86–100% of it.
 *
 * ⚠ **The flows are untouched at any setting in this range.** JOBS, SUBS and OFFERS swing
 * **170–200%** of their level at every scope — they approach zero inside the window, which a
 * stock never does — so the floor never binds on them and the plot is still their own
 * `[min, max]`, to the pixel. The whole change lands on the lines that were not moving.
 *
 * ⚠ **This is a scale, not a filter.** No value is altered, dropped or averaged into its
 * neighbours — a moving average would be the other way to flatten this line, and it would draw
 * figures the data does not contain, which is the same objection `smoothCurve` records against
 * Catmull-Rom. Every point still sits at its own value, the dots still mark them, and the hover
 * bubble still reads them off `point`, so the figures and the shape cannot disagree.
 *
 * ⚠ **It is relative, deliberately.** A fixed floor (say 20 units) would flatten OFFERS running
 * 2 → 3 — a 50% jump — while leaving TOA's noise untouched. Against the level, a one-unit move
 * on a series of threes is a third of its band and still reads as a jump; the same unit on a
 * series of 480s is 0.2% and reads as the flat line it is.
 *
 * One knob, and it is **exported so the tests read it** rather than restating the number: raise
 * it to flatten small movement further, set it to `0` to restore the old edge-to-edge fit
 * exactly. ⚠ Below `1` the reading stops being proportional — the height means a *minimum* swing
 * again, and every series under it is magnified by `1 / MIN_RELATIVE_SPAN`.
 */
export const MIN_RELATIVE_SPAN = 1;

/**
 * Motion (owner request 2026-08-28). The keyframes are in `theme/global.css`; the timings are
 * here because the dots are staggered along the line and their delays are fractions of
 * `DRAW_MS` — one number in one file rather than two that have to agree.
 *
 * Long enough to read as a line being drawn, short enough that a period change does not feel
 * like waiting. `prefers-reduced-motion` cancels both, in the stylesheet.
 */
const DRAW_MS = 620;
const DOT_MS = 240;

/**
 * The four sizes that set how heavy the plot reads. They move together — thin the stroke without
 * thinning these and the dots become beads on a wire, and the halo's tuck goes from a hairline to
 * a gap you notice.
 *
 * `STROKE_WIDTH` went 2.4 → **1.8** (owner request 2026-08-29) for a sharper line. There is a
 * floor here rather than an invitation to keep going: below ~1.5 a curve this shallow starts
 * dropping in and out of the pixel grid on non-retina displays, and a line that fades in patches
 * is worse than a slightly heavy one.
 *
 * The dots are markers, not hit targets — the hover bands at the bottom of the render are what a
 * pointer actually lands on — so they can afford to be quiet. r=3 with a 1px ring put 48 competing
 * marks on a four-series chart and the lines had to fight them; `DOT_R` still says "these are the
 * twelve samples" without shouting. The hovered column swells to `DOT_R_HOVER`, which keeps
 * roughly the 2× it had at the old sizes — the swell has to stay obvious at a glance, since it is
 * the only mark on the plot saying which column the bubble is reporting.
 */
const DOT_R = 2.1;
const DOT_R_HOVER = 4.2;
const STROKE_WIDTH = 1.8;

/**
 * The under-stroke in the panel's own background that makes crossings legible.
 *
 * Each series is scaled independently, so the four lines cross constantly and **none of those
 * crossings mean anything** — they are artefacts of four scales sharing a box, and they read as
 * knots. Drawing every line over a wider stroke the colour of the panel turns each crossing into
 * a clean tuck: the later series passes in front, the earlier one behind. It is a depth cue only.
 * It moves no point and hides no figure.
 *
 * Tied to `color.surface` deliberately: on a panel that is not white this must change with it, or
 * the halo stops being invisible and becomes a white ribbon under every line.
 *
 * It has to stay meaningfully wider than `STROKE_WIDTH` — the tuck a reader sees is the
 * ~1.7px of background either side of the line, and closing that gap silently restores the knots.
 */
const HALO_WIDTH = 5.2;

/** Clearance between the hovered column and the bubble beside it. */
const TIP_GAP = 16;

/**
 * The two rows under the plot — the x axis, and the picked bucket's summary — at **fixed**
 * geometry (owner request 2026-09-03).
 *
 * Fixed because the loading state has to reserve exactly what the loaded state will occupy. The
 * skeleton used to stop at the plot's bottom edge, so every filter change collapsed the card by
 * the height of these two rows and grew it back when the response landed: the panel flinched, the
 * page below it jumped, and on a slow request it did both twice. A skeleton is a promise about
 * the shape of what is coming, and one that is the wrong size is worse than none.
 *
 * Both states lay out from these constants and from `axisRowStyle` / `summaryRowStyle` below, so
 * the reservation cannot drift from the thing reserved. ⚠ Change a font size in either row and
 * the matching height here has to move with it — that is the cost of the guarantee, and it is
 * cheaper than the flinch.
 *
 * `AXIS_ROW_HEIGHT` is the label's own line box (10px mono × 1.2). `SUMMARY_ROW_HEIGHT` is a
 * **minimum**, not a height: the strip wraps on a narrow card, and a hard height would clip the
 * second line rather than let the card grow.
 */
const AXIS_ROW_HEIGHT = 12;
const SUMMARY_ROW_HEIGHT = 18;
/** The gap over the x axis. Was -6, which pulled the row up against the curve. */
const AXIS_ROW_MARGIN = 4;
/** Above and below the strip's hairline, so it reads as a band rather than a fifth line of text. */
const SUMMARY_ROW_GAP = 10;
/** The strip's own hairline, counted because `CHART_BODY_HEIGHT` below is a sum of real pixels. */
const SUMMARY_ROW_BORDER = 1;

/**
 * What the card's **body** occupies, in either reading of the response (owner request 2026-09-09).
 *
 * The plot block is four stacked pieces of fixed geometry — plot, x axis, the strip's hairline,
 * the strip — and this is their **sum**, so the chart view already occupies it by construction and
 * needs no reservation of its own. What did not was the table: ~85px shorter, so switching views
 * shrank the card and grew it back, and every card below it moved twice for a change that altered
 * no figure. The matrix now takes this as its floor and grows its rows into it, which makes the
 * switch a change of *contents* — the card, the pills, the KPI strip and the page all stay exactly
 * where they are.
 *
 * ⚠ It is a **floor**, not a height. The summary strip wraps on a narrow card and must be allowed
 * to (`SUMMARY_ROW_HEIGHT` is itself a minimum for that reason), so the chart view can still grow
 * past this — the table simply can no longer be *shorter* than it, which is the direction that
 * caused the jump. Exported because `<ChartTable>` fills it and the test asserts both views
 * against it rather than restating the number.
 */
export const CHART_BODY_HEIGHT =
  CHART_HEIGHT +
  AXIS_ROW_MARGIN +
  AXIS_ROW_HEIGHT +
  SUMMARY_ROW_GAP * 2 +
  SUMMARY_ROW_BORDER +
  SUMMARY_ROW_HEIGHT;

const axisRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: AXIS_ROW_HEIGHT,
  marginTop: AXIS_ROW_MARGIN,
} as const;

/**
 * How many bars the loading state draws in each row. Neither is a claim about the response — the
 * bucket count is the API's and the series count is the scope's — and neither can change the
 * card's height, which is what the reservation is for. Twelve is every response the product
 * serves; four is Regions, and Delivery's three arrive a moment later one bar narrower.
 */
const PLACEHOLDER_BUCKETS = 12;
const PLACEHOLDER_SERIES = 4;

/**
 * A stand-in legend chip, for the **first** load only — once a response has landed the row keeps
 * the real chips across every later fetch (see `legendSeries`).
 *
 * It exists because the legend row also carries the filter controls, and those re-wrap when the
 * chips beside them appear: without this the very first response still moves the card, which is
 * the one flinch the two reserved rows under the plot cannot cover. The box is the `LegendChip`
 * box — 12px label between 5px paddings and a 1px border — and the width is an average of the
 * five labels rather than any one of them, since nothing here knows yet which series the scope
 * will declare.
 */
const LEGEND_PLACEHOLDER_WIDTH = 64;
const LEGEND_PLACEHOLDER_HEIGHT = 26;

const summaryRowStyle = {
  display: 'flex',
  alignItems: 'center',
  /*
   * Centred under the plot (owner request 2026-09-03), like the legend row in the blueprint's own
   * charts — the strip is a caption on the whole picture, not a continuation of the x-axis row
   * above it, whose left edge belongs to bucket one.
   */
  justifyContent: 'center',
  gap: 18,
  // Wraps rather than scrolls or truncates: the series fit one line at any card width these
  // pages have, and a narrow window should fold rather than clip a figure off the end.
  flexWrap: 'wrap',
  minHeight: SUMMARY_ROW_HEIGHT,
  marginTop: SUMMARY_ROW_GAP,
  paddingTop: SUMMARY_ROW_GAP,
} as const;

/**
 * The legend's ALL chip is **neutral**, exactly as `<RegionPills>`' is: All is the absence of a
 * pick, so it borrows no series' hue. The two strips sit one click apart on the same page and a
 * reader should not have to learn the affordance twice.
 *
 * Its dot is the one thing that does carry the hues — a hard-stop conic slice per drawn series —
 * because a blank grey dot beside four coloured ones reads as a fifth, unlabelled line rather
 * than as "all of the above".
 */
const ALL_ACCENT = color.inkMuted;

export interface LineChartProps {
  data: ChartData | null;
  title: string;
  titleColor?: string;
  loading?: boolean;
  /**
   * Rendered inline with the title — the blueprint's `✕ clear` chip on Region Detail.
   * Sits in the title row rather than a row of its own, which is what keeps the card from
   * growing a band of dead space whenever nothing is selected.
   */
  titleAdornment?: ReactNode;
  /**
   * Whether to draw the title row at all. `true` everywhere except a chart that has been folded
   * into a card that already carries its subject's name as a heading — Delivery's entity card.
   *
   * `title` is still **required** when this is off: it is the chart's accessible name and its
   * data table's caption, so suppressing the row must not suppress the name. Hiding the visible
   * copy is the only thing this does.
   */
  showTitle?: boolean;
  /**
   * Pushed to the right of the legend, on the **same** row. The blueprint's Region Detail card
   * puts the series chips and the filter controls in one flex row with `margin-left:auto` on the
   * controls; passing them in keeps that single row a fact of this component rather than
   * something each page re-creates above the chart and hopes lines up.
   */
  controls?: ReactNode;
  /**
   * Whether the card offers the **table** reading of the same response — the two-glyph toggle at
   * the end of the controls row, and `<ChartTable>` in the plot's place (owner request
   * 2026-09-09).
   *
   * Opt-**in**, and all three charts opt in since 2026-09-11 — the two region cards from the
   * start, Delivery's entity card when the owner made the call the paragraph below was waiting
   * on.
   *
   * It stays a prop rather than becoming always-on, because the table replaces the plot inside
   * whatever card the caller built around this component and the caller is the only one who
   * knows what else is in it. Delivery is the case that made the distinction: it folds the chart
   * into an entity header whose tile strip is read *at the chart's lens* (PRD-Delivery §11.6),
   * so the objection was that swapping the plot away leaves six lensed tiles with no visible
   * line to be the lens of. What answered it is that the lens never lived in the plot — the
   * Source / Targeting / Specialty controls are in the header row, which both views share, and
   * the card prints `Follows the chart · …` between the two. A caller that carried neither
   * would be taking that objection on for real, which is why the decision stays theirs.
   */
  tableView?: boolean;
  /**
   * Actions for the card as a whole — today the jobs export (FR-088, 2026-09-12).
   *
   * ⚠ **A prop rather than markup inside the card, and in the HEADER.** The header is shared by
   * both views, so this control is reachable from the plot as well — which the matrix's own
   * column never is. It covers the card's whole subject: the region, both datasets.
   *
   * ⚠ **It is NOT superseded by `onExportRow`** (FR-093, 2026-09-14) and the two are different
   * questions. This one exports the region — jobs *and* submissions, two tabs — from either view;
   * the row controls each export one dataset and exist only while the matrix is on screen. A
   * reader on the plot has no rows to press.
   *
   * ⚠ The note this replaces said the matrix column "does not fit" and cited the 1,076px floor.
   * That arithmetic was correct and the column was refused on it until the owner chose, on
   * 2026-09-14, to pay for it by narrowing the bucket tracks instead — see `ACTION_COLUMN_WIDTH`
   * in `ChartTable.tsx`. The floor is still asserted by `tests/linechart.tableview.test.tsx`.
   *
   * It sits **before** the view toggle in the same cluster: an action on the data comes ahead of
   * a switch between two drawings of it, and both are after the filters, which are the only
   * things on this row that move a figure.
   */
  actions?: ReactNode;
  /**
   * Per-row export controls for the matrix — see `ChartTableProps.onExportRow` (FR-093).
   *
   * ⚠ **Passed straight through and used by the table view alone.** The plot has no rows, so this
   * is the one prop on this component that does nothing in the default view; that is why it is
   * named for the row rather than for the chart, and why a caller that sets it should also set
   * `tableView` — without it the control is unreachable.
   */
  onExportRow?: (key: ChartRowKey) => ReactNode;
  /**
   * `Envelope.syncedAt` — when the daily job promoted the snapshot these points came from.
   *
   * Feeds the hover bubble's coverage line and nothing else (FR-087). It is what the newest
   * bucket's window ends at: the served `end` is that bucket's query boundary, the end of the
   * as-of day, while the rows stop at the moment the job read BigQuery. Optional, and a chart
   * without it simply prints the bucket's own bounds — the window is coarser, not wrong.
   */
  syncedAt?: string | null;
}

interface Scaled {
  key: SeriesKey;
  color: string;
  kind: 'stock' | 'period';
  points: Array<{ x: number; y: number; value: number | null; point: ChartPoint }>;
  /** The whole curve. The halo traces it, and so does the area fill. */
  path: string;
  /** The curve minus a partial final segment — everything that is drawn solid. */
  solid: string;
  /** That final segment alone, drawn dashed. `null` when there is nothing to qualify. */
  tail: string | null;
  /** `path` closed down to the bottom edge, for the isolated-series fill. */
  area: string;
}

/**
 * The rounded line (owner request 2026-08-28). A **monotone** cubic — Fritsch–Carlson — and
 * deliberately not the Catmull-Rom spline that every "smooth line chart" snippet reaches for.
 *
 * The difference is the whole reason this is hand-written. Catmull-Rom takes each point's tangent
 * from its two neighbours unconditionally, so at a peak it arcs *above* the peak: on a series that
 * goes 40 → 96 → 41 the curve crests somewhere around 104. This chart draws no Y axis and no
 * gridlines, so its shape is the only reading a user gets — a crest higher than any month in the
 * data is the chart asserting a figure that never happened, and there is nothing on screen to
 * catch it. Zeroing the tangent at a turning point (and clamping the rest) makes every segment
 * monotone, which means the curve stays inside the two values it joins. Smoothing becomes a
 * rendering choice rather than a claim.
 *
 * `dx / 3` is not a tunable smoothness dial: it is the exact Hermite→Bézier conversion, so the
 * curve leaves each point along the tangent computed for it. Shrinking it would flatten the
 * curve back toward the old straight segments without making it any safer.
 *
 * It hands back the move and the segments **as a list** rather than one `d` string, and that is
 * the only reason the split exists: the final segment is pulled out and drawn dashed for the
 * in-progress bucket (see `scaleSeries`, FR-085). Joining them here would put the dash back on the
 * whole line or take it off entirely.
 */
function smoothCurve(pts: ReadonlyArray<{ x: number; y: number }>): {
  move: string;
  segments: string[];
} {
  const n = pts.length;
  if (n === 0) return { move: '', segments: [] };

  const fmt = (v: number) => v.toFixed(1);
  const at = (i: number) => pts[i]!;
  if (n === 1) return { move: `M${fmt(at(0).x)},${fmt(at(0).y)}`, segments: [] };

  // Secant slope of each gap. x is strictly increasing (`index * stepX`), so no gap is ever zero
  // wide and none of the divisions here or below can blow up.
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    slope.push((at(i + 1).y - at(i).y) / (at(i + 1).x - at(i).x));
  }

  // One tangent per point: the mean of its two secants — but **zero** where they disagree in
  // sign, which is exactly a peak or a trough. That single line is what forbids the overshoot.
  const tangent: number[] = new Array(n);
  tangent[0] = slope[0]!;
  tangent[n - 1] = slope[n - 2]!;
  for (let i = 1; i < n - 1; i += 1) {
    const before = slope[i - 1]!;
    const after = slope[i]!;
    tangent[i] = before * after <= 0 ? 0 : (before + after) / 2;
  }

  // Fritsch–Carlson limiter. Zeroing the turning points is not sufficient on its own: a sharp
  // step between two shallow runs still leaves a tangent steep enough to bulge past its own
  // segment. Clamping each pair into the circle of radius 3 about the segment's slope is the
  // condition that makes the segment monotone.
  for (let i = 0; i < n - 1; i += 1) {
    const s = slope[i]!;
    if (s === 0) {
      // A flat gap: both ends must be flat too, or the curve wanders off a horizontal run.
      tangent[i] = 0;
      tangent[i + 1] = 0;
      continue;
    }
    const a = tangent[i]! / s;
    const b = tangent[i + 1]! / s;
    const h = Math.hypot(a, b);
    if (h > 3) {
      const scale = 3 / h;
      tangent[i] = scale * a * s;
      tangent[i + 1] = scale * b * s;
    }
  }

  const segments: string[] = [];
  for (let i = 0; i < n - 1; i += 1) {
    const from = at(i);
    const to = at(i + 1);
    const third = (to.x - from.x) / 3;
    segments.push(
      ` C${fmt(from.x + third)},${fmt(from.y + tangent[i]! * third)} ${fmt(to.x - third)},${fmt(
        to.y - tangent[i + 1]! * third,
      )} ${fmt(to.x)},${fmt(to.y)}`,
    );
  }
  return { move: `M${fmt(at(0).x)},${fmt(at(0).y)}`, segments };
}

function scaleSeries(
  key: SeriesKey,
  points: ChartPoint[],
  seriesColor: string,
  kind: 'stock' | 'period',
  width: number,
): Scaled {
  const values = points.map((p) => p[key] ?? null);
  const numeric = values.filter((v): v is number => v !== null && Number.isFinite(v));

  const max = numeric.length > 0 ? Math.max(...numeric) : 0;
  const min = numeric.length > 0 ? Math.min(...numeric) : 0;

  /**
   * The domain: the data's own range, or `MIN_RELATIVE_SPAN` of its level, whichever is wider —
   * so at the current setting of `1` a series that moves less than its own level occupies exactly
   * the fraction of the height its movement is worth, and the reader can read percent off the
   * plot without a Y axis to help them.
   *
   * Centred on the middle of the data rather than anchored at `min`, which is what makes the two
   * cases one expression instead of a branch: when the data is the wider of the two, `centre −
   * span / 2` **is** `min` and the mapping is identical to the old one, to the pixel. When the
   * floor wins, the series floats in the middle of the plot with equal air above and below —
   * where a floor anchored at `min` would pin a flat line to the bottom inset and read as a
   * series that had collapsed.
   *
   * Every value therefore still lands inside `[0, 1]`; there is nothing to clamp. `|| 1` is the
   * flat-series guard as before — a series that never moves has no level to take a fraction of
   * when its values are all zero, and would divide by zero.
   */
  const centre = (max + min) / 2;
  const span = Math.max(max - min, Math.abs(centre) * MIN_RELATIVE_SPAN) || 1;
  const floor = centre - span / 2;

  // Edge to edge: the first point sits at x=0 and the last at x=width.
  const stepX = points.length > 1 ? width / (points.length - 1) : 0;

  const scaled = points.map((point, index) => {
    const value = point[key] ?? null;
    const ratio = value === null ? 0.5 : (value - floor) / span;
    return {
      x: points.length > 1 ? index * stepX : width / 2,
      y: CHART_HEIGHT - PAD_Y - ratio * (CHART_HEIGHT - PAD_Y * 2),
      value,
      point,
    };
  });

  // Nulls are dropped before smoothing, as they were before it: a missing bucket is bridged by
  // one longer curve rather than breaking the line in two.
  const drawn = scaled.filter((p) => p.value !== null);
  const curve = smoothCurve(drawn);
  const path = curve.move + curve.segments.join('');

  /**
   * **The in-progress bucket, and why only some series are dashed** (owner decision 2026-09-10,
   * FR-085 — restoring the treatment that stood from 2026-08-28 to 2026-09-02).
   *
   * The last bucket ends at the as-of date, not at a calendar boundary (PRD §8.4). For a period
   * SUM that guarantees a dive at the right edge: JOBS peaks at the previous month and lands on
   * the floor at the current one, which is a few days of a 31-day month and nothing more. Drawn
   * identically to a finished month it reads as the business falling off a cliff, and with no Y
   * axis and no gridlines there is nothing else on screen to correct the impression. Dashed, it
   * reads as a period still in progress — which is what it is.
   *
   * ⚠ **This dash is the whole reason the bucket is drawn at all.** Between 2026-09-08 and
   * 2026-09-10 the chart dropped the in-progress period instead (FR-082); bringing the bucket
   * back without the dash restores the dive and nothing that explains it, which is the state that
   * produced the original complaint.
   *
   * **TOA is never dashed**, and the test is on `kind` rather than on `isPartial` for exactly
   * that reason: `isPartial` is true of TOA's final point too, but a stock is evaluated at
   * `bucket.end` alone, so "as of today" is a complete and true answer — it is the same figure
   * the `ACTV` column and the Active KPI card report. Qualifying it would manufacture a doubt the
   * data does not have. Widening this test to `isPartial` alone is the mistake to guard against.
   */
  const last = drawn[drawn.length - 1];
  const beforeLast = drawn[drawn.length - 2];
  const dashTail =
    kind === 'period' && last !== undefined && beforeLast !== undefined && last.point.isPartial;

  return {
    key,
    color: seriesColor,
    kind,
    points: scaled,
    path,
    solid: dashTail ? curve.move + curve.segments.slice(0, -1).join('') : path,
    tail: dashTail
      ? `M${beforeLast.x.toFixed(1)},${beforeLast.y.toFixed(1)}${
          curve.segments[curve.segments.length - 1]
        }`
      : null,
    // Closed to the bottom EDGE, not to the bottom inset: the gradient fades out before it gets
    // there, so a floor at `CHART_HEIGHT - PAD_Y` would show as a hard line across the panel.
    area:
      drawn.length > 1 && last !== undefined
        ? `${path} L${last.x.toFixed(1)},${CHART_HEIGHT} L${drawn[0]!.x.toFixed(
            1,
          )},${CHART_HEIGHT} Z`
        : '',
  };
}

/**
 * One legend chip. Extracted (2026-08-30) so the ALL chip and the four series chips cannot drift:
 * they sit in the same row, and a padding or radius tweak applied to only one would read as a
 * rendering fault rather than as a design.
 *
 * `dot` is separate from `accent` for exactly one caller — ALL, whose ring is neutral while its
 * dot carries every series hue.
 */
function LegendChip({
  label,
  accent,
  active,
  dimmed = false,
  dot,
  accessibleName,
  onClick,
}: {
  label: string;
  accent: string;
  active: boolean;
  dimmed?: boolean;
  dot: string;
  accessibleName: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={accessibleName}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 11px',
        borderRadius: 20,
        cursor: 'pointer',
        border: `1px solid ${active ? accent : color.borderStrong}`,
        background: active ? tint(accent, 8) : color.surface,
        fontFamily: 'inherit',
        // Dimmed, not hidden: the chip is how the line comes back.
        opacity: dimmed ? 0.5 : 1,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 9,
          height: 9,
          borderRadius: '50%',
          background: dot,
        }}
      />
      <span
        style={{
          fontSize: fontSize.body,
          fontWeight: 600,
          color: active ? accent : color.inkRow,
        }}
      >
        {label}
      </span>
    </button>
  );
}

export function LineChart({
  data,
  title,
  titleColor,
  loading,
  titleAdornment,
  showTitle = true,
  controls,
  tableView = false,
  actions,
  onExportRow,
  syncedAt,
}: LineChartProps) {
  /**
   * Which series the legend has picked out, **empty meaning all of them** (PRD §6.2, "empty
   * metric selection shows all series").
   *
   * A list rather than one key, so TOA and SUBS can be compared without the other two lines in
   * the way — the same shape, and the same reasoning, as the region pills on Regions Overview.
   * The empty state carries the "show everything" meaning rather than a separate flag, which is
   * what makes the chart impossible to empty by deselecting: unpick the last chip and you are
   * back to the default rather than looking at a blank plot.
   */
  const [selected, setSelected] = useState<readonly SeriesKey[]>([]);
  /**
   * Which reading of the response is on screen — the plot, or the same figures as rows (owner
   * request 2026-09-09).
   *
   * **Plain component state, and it resets on unmount.** It is deliberately not routed through
   * `state/persist.ts`: on these pages the chart's own filters are plain state too (only Delivery
   * passes a `storageKey`), so a view that survived navigation while the Specialty beside it did
   * not would be the odd one out — and PRD §6.4's defaults are what a fresh page must present
   * (SC-010). Nothing about it is a filter: no figure changes, so a stale value could never put a
   * misread number in front of anyone, which is the risk persistence is weighed against.
   */
  const [view, setView] = useState<ChartView>('chart');
  /**
   * The hovered BUCKET — no series (owner request 2026-08-29).
   *
   * It used to carry the line the pointer was nearest, because that decided whose breakdown the
   * bubble showed. The bubble now decomposes every visible series at once, so there is nothing
   * left for a vertical pick to decide: the column is the unit of interaction, and isolating a
   * single line is what the legend chips are for.
   */
  const [hovered, setHovered] = useState<{ index: number } | null>(null);
  /**
   * Which bucket the summary strip is reading, **`null` meaning the latest one** (owner request
   * 2026-09-03) — so the page opens on the newest bucket without anything having to say so. Since
   * FR-085 that bucket is the period **in progress** again, which is why the strip prints
   * `· partial` beside its label: the strip opens on a figure that is still moving, and the label
   * is what keeps it from being read as a finished period.
   *
   * `null` rather than `points.length - 1` so the default survives every reshape of the response:
   * switch Weekly → Quarterly, narrow a filter, or let a new snapshot land, and "the current
   * bucket" is still the current bucket with no effect watching `data` to re-pin it. A stored
   * index is clamped where it is read, for the same reason — twelve buckets today is not a
   * promise about tomorrow.
   *
   * Deliberately **separate state from `hovered`**. Hover is a pointer passing through and it
   * answers with the bubble; a click is a pick that stays put, which is the whole point of the
   * strip — it is still readable with the pointer somewhere else on the page. Both name their own
   * bucket on screen, so the two readings can never be taken for one.
   */
  const [pinned, setPinned] = useState<number | null>(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH);

  /**
   * The area gradient needs a document-unique id: Region Detail renders this component beside a
   * second one, and two `<linearGradient id="area">` would have the first silently win for both.
   * The colons React puts in `useId` are legal in an id and fine inside `url(#…)`, but they break
   * `querySelector`, so they come out — a test looking for the gradient should be able to select
   * it.
   */
  const areaId = `${useId().replace(/:/g, '')}-area`;

  /**
   * A **callback** ref, not `useRef` + `useEffect([])`.
   *
   * The plot only exists in the loaded branch, so on a slow first request the effect would run
   * against a null node and never re-run when the data arrived — the chart would keep drawing at
   * `FALLBACK_WIDTH` until something else forced a resize. A callback ref fires whenever the node
   * attaches or detaches, which is exactly when there is something to measure.
   *
   * `ResizeObserver` is guarded because jsdom has neither it nor a layout engine: under test
   * `clientWidth` is 0, the guard below keeps the fallback, and the geometry stays deterministic.
   */
  const observer = useRef<ResizeObserver | null>(null);
  const plotRef = useCallback((node: HTMLDivElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) return;

    const measure = () => {
      const next = node.clientWidth;
      // 0 means "not laid out yet" (or jsdom), which is not a width worth drawing at.
      if (next > 0) setWidth(next);
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    observer.current = ro;
  }, []);

  /** Add or remove one series. Removing the last one lands on `[]`, which is all of them. */
  const toggleSeries = useCallback((key: SeriesKey) => {
    setSelected((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );
  }, []);

  /**
   * The ALL chip clears the selection; it does **not** fill it with every key.
   *
   * `[]` is the canonical "show everything" state (see `selected` above), and the two are not
   * interchangeable on the *next* click: from `[]` a chip isolates that one series, which is what
   * the legend is for, while from a full list it would subtract one and leave three. Same picture,
   * opposite next move.
   */
  const selectAll = useCallback(() => setSelected([]), []);

  /**
   * What counts as new data — and, load-bearingly, what does not.
   *
   * The draw-in is a CSS animation, so it runs when its element **mounts** and ignores later
   * attribute changes. That default is the right one: `d` is rewritten on every ResizeObserver
   * tick, and a chart that re-draws itself while the window is being dragged is unusable. Hover
   * is excluded for the same reason — traversing the twelve dots re-renders this component twelve
   * times, and each one would restart four lines.
   *
   * So the animation is put under a React `key`, and this string is that key: the buckets, the
   * declared series, and the figures. Change the period, the grain, or a filter and the chart
   * re-draws; resize the window or move the pointer and it stays exactly where it is.
   */
  const epoch = useMemo(() => {
    if (!data) return 'none';
    const keys = Object.keys(data.series).join();
    // The values too, not just the bucket keys: a Source or Specialty filter narrows the figures
    // without moving the twelve buckets, and that is a re-draw.
    const figures = data.points
      .map((p) => `${p.key}:${p.toa}/${p.jobs ?? ''}/${p.subs}/${p.offers}`)
      .join();
    return `${keys}~${figures}`;
  }, [data]);

  const scaled = useMemo<Scaled[]>(() => {
    if (!data) return [];
    // Only the series the response actually declared. A key absent from `series` is a line that
    // does not exist for this scope, not a line whose values happen to be null.
    return SERIES_ORDER.flatMap((key) => {
      const meta = data.series[key];
      if (!meta) return [];
      /*
       * A `display: 'table'` series is a matrix ROW with no line (FR-D26) — Delivery's backouts,
       * starts and unique submissions. It is skipped here and picked up by `tableRows` below.
       *
       * The test is on the flag rather than on `color == null`, even though the two coincide on
       * the wire today: the flag is what the API is asserting, and a null hue on a drawn series
       * should show up as an unstyled line to be fixed, not as a silently deleted one.
       */
      if (meta.display === 'table' || meta.color === null) return [];
      /*
       * The hue the API sent becomes the FALLBACK, not the value (2026-08-29).
       *
       * `backend/src/domain/series.ts` owns which colour means which series, and light mode still
       * renders exactly what it sends. `--c-series-*` is undefined in the light palette, so the
       * fallback wins. Dark mode defines it, because the same #1d4ed8 that reads as TOA on white
       * is nearly invisible on slate — the server owns identity, the client owns legibility
       * against the background it chose. Resolving it here means every use downstream (stroke,
       * dot, legend chip, tooltip figure) follows without knowing any of this.
       */
      const themed = `var(--c-series-${key}, ${meta.color})`;
      return [scaleSeries(key, data.points, themed, meta.kind, width)];
    });
  }, [data, width]);

  /**
   * What the LEGEND draws — `scaled`, or the last non-empty set of series while a response is in
   * flight (owner request 2026-09-08).
   *
   * The chips are the one part of this card whose shape was still not reserved. `scaled` is empty
   * in the loading branch, so every filter change emptied the legend row, the four segmented
   * controls beside it re-wrapped onto one line, and the card lost ~35px and grew it back a
   * moment later — the flinch `AXIS_ROW_HEIGHT` and `SUMMARY_ROW_HEIGHT` were introduced to end,
   * arriving from the row above the plot instead of the two below it. Keeping the previous series
   * holds the row at exactly the width and height the response will land in.
   *
   * They stay **live**, not disabled: `selected` is the chart's own state and survives the fetch,
   * so a chip pressed mid-request applies the moment the points arrive. Nothing here reads a
   * figure — a `Scaled` carries a key and a hue, and the plot is drawn from `scaled` alone — so
   * this can never put a stale number on screen, which is the reason the *data* is not held over
   * the same way.
   *
   * ⚠ A render-phase ref write, deliberately, not an effect: the legend has to be right on the
   * **first** paint of the loading branch, and an effect would run after it and flash the empty
   * row anyway. It is idempotent, so a double render under StrictMode lands on the same value.
   */
  const lastLegend = useRef<Scaled[]>([]);
  if (scaled.length > 0) lastLegend.current = scaled;
  const legendSeries = scaled.length > 0 ? scaled : lastLegend.current;

  /** Every declared line is on the plot — by default (`[]`) or because all of them were picked. */
  const allShown = selected.length === 0 || selected.length === legendSeries.length;

  /**
   * The lines the legend has left showing — the plot's rule (PRD §6.2, "an empty selection shows
   * all series"), applied to the table's **columns**.
   *
   * Read off `legendSeries` rather than `scaled` so the table holds its column set across a fetch
   * exactly as the legend holds its chips, and so the two can never disagree about which series
   * exist. `visible` below is the same computation over `scaled`, one step later, because by then
   * the response is known to be present; this one has to survive the loading branch.
   */
  const visibleLegend =
    selected.length > 0 ? legendSeries.filter((s) => selected.includes(s.key)) : legendSeries;

  /**
   * Every row the matrix draws: the lines the legend has left showing, then the response's
   * **table-only** series (owner request 2026-09-11, FR-D26).
   *
   * ⚠ **The legend does not govern the second group, and must not.** A chip is the control for a
   * line; these rows have none, so a reader who isolated SUBS and lost BACKOUTS with it would
   * have no way to bring it back. Isolating a line therefore narrows the lines and leaves the
   * table-only rows in place — which is also the honest reading of what the chips do: they pick
   * what is *drawn*.
   *
   * Read off `data` rather than `legendSeries`, because these rows never enter `scaled`: they are
   * not lines, and the loading branch has no table to hold a column set for.
   */
  const tableRows = useMemo<ChartTableSeries[]>(() => {
    const rows: ChartTableSeries[] = visibleLegend.map((line) => ({
      key: line.key,
      kind: line.kind,
      color: line.color,
      // A drawn series sums unless it is a stock — the rule that predates the flag, kept as the
      // default so the region responses, which send no `sums`, behave exactly as they did.
      sums: data?.series[line.key]?.sums ?? line.kind !== 'stock',
    }));

    for (const key of ROW_ORDER) {
      const meta = data?.series[key];
      if (!meta || meta.display !== 'table') continue;
      rows.push({
        key,
        kind: meta.kind,
        // No hue: the dot ties a row to its line, and this row has none.
        color: null,
        sums: meta.sums ?? meta.kind !== 'stock',
      });
    }

    return rows;
  }, [visibleLegend, data]);

  /**
   * The ALL dot: one hard-stop slice per drawn series, so it is three slices on Delivery and four
   * on Regions without either page saying so. Hard stops rather than a blend — a gradient between
   * two series hues produces a third colour that is not any line's.
   */
  const allDot = useMemo(() => {
    const step = 100 / legendSeries.length;
    const stops = legendSeries
      .map((series, i) => `${series.color} ${i * step}% ${(i + 1) * step}%`)
      .join(', ');
    return `conic-gradient(${stops})`;
  }, [legendSeries]);

  /**
   * Title and the legend/controls row, rendered in EVERY state.
   *
   * Kept out of the early returns on purpose: the filter controls live in this row, and a
   * chart that drops its own filters while loading is a chart the user cannot steer out of a
   * slow request. The legend is drawn from `legendSeries` rather than `scaled` for the same
   * family of reason one step further — see the note there — so the row keeps its chips, and
   * therefore its wrap, across the fetch.
   */
  const header = (
    <>
      {showTitle ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              textAlign: 'center',
              fontSize: fontSize.sectionTitle,
              fontWeight: 700,
              color: titleColor ?? color.ink,
            }}
          >
            {title}
          </div>
          {titleAdornment}
        </div>
      ) : null}

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          marginBottom: 6,
        }}
      >
        {/*
         * One click back to every line (owner request 2026-08-30).
         *
         * Deselecting chips one at a time already lands on all of them, but from an isolated
         * series that is three clicks on Regions and two on Delivery, and the last one only
         * works because "none" secretly means "all" — a rule the strip never showed. This is
         * that rule, given a face.
         *
         * Rendered only where there is a choice to make: there is nothing to pick from before
         * the first response lands, and a single-series scope has nothing for ALL to mean.
         */}
        {legendSeries.length > 1 ? (
          <LegendChip
            label="ALL"
            accent={ALL_ACCENT}
            // Not `selected.length === 0`. Pick every chip by hand and every line is drawn, which
            // is what this chip claims; leaving it dark there would have it contradict the plot.
            active={allShown}
            dot={allDot}
            accessibleName={`All ${legendSeries.length} series`}
            onClick={selectAll}
          />
        ) : null}

        {legendSeries.map((series) => {
          const isSelected = selected.includes(series.key);
          // Only meaningful while a selection is active: with none, every line is drawn and no
          // chip is excluded. It is what lets the legend answer "which lines am I looking at"
          // once the chips stop being all-or-one.
          const isExcluded = selected.length > 0 && !isSelected;
          return (
            <LegendChip
              key={series.key}
              label={SERIES_LABEL[series.key]}
              accent={series.color}
              active={isSelected}
              dimmed={isExcluded}
              dot={series.color}
              accessibleName={`${SERIES_FULL[series.key]}${
                series.kind === 'stock' ? ' — an as-of count at each bucket end' : ''
              }`}
              onClick={() => toggleSeries(series.key)}
            />
          );
        })}

        {loading && legendSeries.length === 0
          ? Array.from({ length: PLACEHOLDER_SERIES + 1 }, (_unused, index) => (
              <span
                key={index}
                className="skeleton"
                aria-hidden="true"
                style={{
                  display: 'block',
                  width: LEGEND_PLACEHOLDER_WIDTH,
                  height: LEGEND_PLACEHOLDER_HEIGHT,
                  borderRadius: 20,
                }}
              />
            ))
          : null}

        {controls || tableView || actions ? (
          <div
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            {controls}
            {/*
              After the filters and set apart by the wider gap: it changes nothing about the
              numbers, only their shape, so it must not read as one more filter.
            */}
            {tableView ? (
              <div style={{ marginLeft: controls ? 6 : 0 }}>
                <ChartViewToggle
                  value={view}
                  onChange={setView}
                  bucketCount={data?.points.length ?? null}
                />
              </div>
            ) : null}
            {/*
              ⚠ **The export is LAST on every control row in the product** (owner request
              2026-09-13) — this one, Region Performance's, By Client's and both of Delivery's.
              It used to sit between the filters and the view toggle here, which left it in a
              different slot on the chart card than on the three table headers, so a reader who
              had learned where it lives on one card had to hunt for it on the next.

              It is the rightmost control rather than the toggle for a reason worth keeping: the
              export is the only control in this row that *leaves the page*, and the last position
              is where an action belongs once the controls that shape what it exports are all to
              its left. `tests/controlrow.order.test.tsx` holds the rule across all four surfaces.

              Inside this cluster rather than beside it, so it wraps with the controls instead of
              being stranded alone on a narrow card.
            */}
            {actions ? (
              <div style={{ marginLeft: controls || tableView ? 6 : 0 }}>{actions}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );

  /**
   * The table reading — the same response, one row per bucket, in the plot's place.
   *
   * **Before the loading and empty branches**, and that is the whole of the state handling here:
   * `<ChartTable>` renders its own reservation (twelve rows of skeleton bars in the same boxes the
   * figures will occupy) and its own empty message, so the three cases below do not each need a
   * table-shaped copy. `data` is nulled while a request is in flight for the reason the plot's
   * data is not held over: a stale figure under a live filter label is exactly the misreading
   * FR-034 is about — and unlike the legend's chips, every cell here *is* a figure.
   *
   * The header row is the same one, so the legend, the filters and the toggle itself stay put
   * across the switch: nothing but the body below them changes.
   */
  if (view === 'table') {
    return (
      <div aria-busy={loading ? 'true' : undefined}>
        {header}
        {/*
          The table occupies exactly what the plot block does, and fades in on the way there.

          `minHeight` is the half of that which matters: the plot, x axis and summary strip come
          to `CHART_BODY_HEIGHT` between them and the matrix is ~85px shorter, so the card used to
          shrink on the way into this view and grow back on the way out — every card below it
          moving twice for a change that alters no figure. Centring is what the table does with
          the room left over: four series fill the reservation almost exactly, and a
          legend-isolated single row sits in the middle of the card rather than against its top
          edge with the plot's height empty beneath it.

          The fade needs no key. This subtree exists only while the view is `table`, so it mounts
          on the switch and `.chart-view-in` runs then — and the plot has always drawn itself in,
          so the other direction was already a transition rather than a cut.
        */}
        <div
          className="chart-view-in"
          style={{
            minHeight: CHART_BODY_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <ChartTable
            data={loading ? null : data}
            loading={loading}
            title={title}
            series={tableRows}
            bodyHeight={CHART_BODY_HEIGHT}
            syncedAt={syncedAt}
            onExportRow={onExportRow}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    /*
     * The **whole** card, at the height it will be when the response lands — plot, x axis and
     * summary strip (owner request 2026-09-03).
     *
     * It used to be the plot alone, so a filter change dropped ~40px off the card and put it back
     * a moment later; every card below shifted with it, twice. The header is already outside this
     * branch for the same family of reason — a chart that drops its own filters while loading is
     * one the reader cannot steer out of a slow request.
     *
     * `PLACEHOLDER_SERIES` is four because Regions draws four. Delivery draws three and briefly
     * shows one bar too many, which costs nothing: the count sets the row's *width*, and the
     * height — the only thing that can make the card flinch — comes from `summaryRowStyle`.
     */
    return (
      <div aria-busy="true">
        {header}
        <div
          className="skeleton"
          style={{ height: CHART_HEIGHT, borderRadius: 12 }}
          aria-hidden="true"
        />

        <div style={axisRowStyle} aria-hidden="true">
          {Array.from({ length: PLACEHOLDER_BUCKETS }, (_unused, index) => (
            <span
              key={index}
              className="skeleton"
              style={{ width: 26, height: 8, borderRadius: 3 }}
            />
          ))}
        </div>

        <div
          style={{ ...summaryRowStyle, borderTop: `1px solid ${color.borderSubtle}` }}
          aria-hidden="true"
        >
          <span className="skeleton" style={{ width: 128, height: 10, borderRadius: 3 }} />
          {Array.from({ length: PLACEHOLDER_SERIES }, (_unused, index) => (
            <span
              key={index}
              className="skeleton"
              style={{ width: 92, height: 10, borderRadius: 3 }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.points.length === 0) {
    return (
      <div>
        {header}
        <div
          style={{
            /*
             * The reserved body height, not `CHART_HEIGHT` (2026-09-09): an empty chart occupies
             * exactly what a drawn one and the table do, so nothing on the page moves on the way
             * into this state or out of it.
             */
            height: CHART_BODY_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: color.inkSubtle,
            fontSize: fontSize.bodyStrong,
          }}
        >
          No chart data for this selection.
        </div>
      </div>
    );
  }

  // "Empty metric selection shows all series" (PRD §6.2). Any other selection draws exactly
  // the chips that are pressed, in the response's own render order.
  const visible = selected.length > 0 ? scaled.filter((s) => selected.includes(s.key)) : scaled;

  /**
   * One series on screen is the only time an area fill is honest.
   *
   * Four fills would overlap into mud, and since the series are scaled independently the overlaps
   * would not even mean anything. Isolating a series is already what the legend chips do, so the
   * fill costs no new control: pick one chip and the chart deepens into a filled shape.
   */
  const filled = visible.length === 1 ? visible[0]! : null;

  const step = data.points.length > 1 ? width / (data.points.length - 1) : 0;
  const bucketX = (index: number) => (data.points.length > 1 ? index * step : width / 2);

  const hoveredPoint = hovered ? data.points[hovered.index] : null;
  /**
   * The hovered bucket's covered window, shared with `<ChartTable>` through `chartData.ts` so
   * the two readings of one response cannot describe the same bucket differently.
   */
  const bucketWindow = hoveredPoint ? formatBucketWindow(hoveredPoint, syncedAt) : null;

  /**
   * The bucket the summary strip reads, and the one before it — the other end of every change
   * it prints.
   *
   * Clamped rather than trusted: `pinned` is an index picked from whatever response was on screen
   * at the time, and a grain change can hand back a different number of buckets before the click
   * is ever cleared. Out of range, it falls back to the latest bucket, which is where the strip
   * starts anyway.
   */
  const lastIndex = data.points.length - 1;
  const summaryIndex = Math.max(0, Math.min(pinned ?? lastIndex, lastIndex));
  const summaryPoint = data.points[summaryIndex] ?? null;
  const previousPoint = summaryIndex > 0 ? (data.points[summaryIndex - 1] ?? null) : null;

  /**
   * Breakdown rows for ONE series at the hovered bucket (blueprint § tooltip, FR-063).
   *
   * The bubble calls this once per visible series, so every line on the plot is decomposed in the
   * same hover rather than only whichever one the pointer was nearest.
   *
   * JOBS carries Target, Nursing and Allied — and **no Internal/External** (owner decision
   * 2026-08-30, same call as the `#JOBS` column). "How much is open, and how much of it at a
   * target account" is the question; whose book the client sits in is not. The axes stay per
   * series rather than a property of the bubble precisely so one series can differ like this.
   */
  const rowsFor = (key: SeriesKey, index: number, total: number | null) =>
    data.breakdown
      ? breakdownRows({
          total,
          internal: data.breakdown.internal[index]?.[key] ?? null,
          target: data.breakdown.target[index]?.[key] ?? null,
          nursing: data.breakdown.nursing[index]?.[key] ?? null,
          allied: data.breakdown.allied[index]?.[key] ?? null,
          includeSource: key !== 'jobs',
        })
      : [];

  return (
    <div>
      {header}

      {/*
        The measured box. `position: relative` anchors the tooltip, and `onMouseLeave` lives HERE
        rather than on each hover band: leaving one band to enter its neighbour fires leave then
        enter, which would clear the hover for one render and flicker the tooltip off and back on
        every bucket the pointer crosses.
      */}
      <div ref={plotRef} style={{ position: 'relative' }} onMouseLeave={() => setHovered(null)}>
        <svg
          viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
          // 1:1 with the box, so the drawing never letterboxes. `overflow: visible` lets the
          // first and last dots sit on the edges without being clipped in half.
          style={{ width: '100%', height: CHART_HEIGHT, display: 'block', overflow: 'visible' }}
          role="img"
          aria-label={`${title}: ${visible
            .map((s) => SERIES_LABEL[s.key])
            .join(', ')} over ${data.points.length} points, oldest to newest`}
        >
          {filled ? (
            <defs>
              {/* Fades to nothing well before the bottom edge, so the fill reads as depth under
                  the line rather than as a solid block with a floor of its own. */}
              <linearGradient id={areaId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" style={{ stopColor: filled.color, stopOpacity: 0.28 }} />
                <stop offset="72%" style={{ stopColor: filled.color, stopOpacity: 0.04 }} />
                <stop offset="100%" style={{ stopColor: filled.color, stopOpacity: 0 }} />
              </linearGradient>
            </defs>
          ) : null}

          {/*
            The picked bucket's guide (owner request 2026-09-03) — the mark that replaced the
            label underline, and the reason the two states are still distinguishable now that
            both labels are simply dark.

            **Dotted where the hover guide is dashed**, on purpose: both are drawn at once
            whenever the pointer is on a different column, and a reader has to be able to tell
            the standing pick from the pointer passing through. Same hairline colour, because
            it is the same kind of object and a second guide colour would read as a third state.

            ⚠ This is one guide at one column, NOT a grid. PRD §6.2 has no gridlines and no Y
            axis; a line per bucket would be exactly that, arriving by the back door.
          */}
          <line
            className="chart-guide"
            data-guide="pinned"
            x1={bucketX(summaryIndex)}
            x2={bucketX(summaryIndex)}
            y1={PAD_Y * 0.5}
            y2={CHART_HEIGHT - PAD_Y * 0.5}
            strokeWidth="1"
            strokeDasharray="1 5"
            strokeLinecap="round"
            style={{ pointerEvents: 'none' }}
          />

          {/* First, so it sits UNDER every line: a guide that cut across the curves it points at
              would be one more thing crossing them. */}
          {hovered ? (
            <line
              className="chart-guide"
              x1={bucketX(hovered.index)}
              x2={bucketX(hovered.index)}
              y1={PAD_Y * 0.5}
              y2={CHART_HEIGHT - PAD_Y * 0.5}
              strokeWidth="1"
              strokeDasharray="3 4"
              style={{ pointerEvents: 'none' }}
            />
          ) : null}

          {/*
            The `key` is the whole point: remounting is what replays the draw-in, and it happens
            only when the figures change. One wrapper rather than an epoch on each series, so a
            legend toggle still animates just the line that appeared — that `<g>` is new, this
            one is not. See the note on `epoch`.
          */}
          <g key={epoch}>
            {filled ? (
              <path
                className="chart-area"
                data-area={filled.key}
                d={filled.area}
                fill={`url(#${areaId})`}
                style={{ animationDuration: `${DRAW_MS}ms` }}
              />
            ) : null}

            {/*
              No dimming on hover (owner request 2026-08-29). Fading three lines to pick out a
              fourth made sense while the bubble decomposed only that fourth one; now that it
              reports every series at the bucket, a highlight that moves with the pointer's
              *height* would change nothing on screen except itself — it would read as a glitch.
              The swollen column and the guide line are the hover emphasis; the legend chips are
              how a reader isolates one line.
            */}
            {visible.map((series) => {
              return (
                <g key={series.key} className="chart-series">
                  <path
                    className="chart-line chart-halo"
                    aria-hidden="true"
                    d={series.path}
                    pathLength={1}
                    style={{ animationDuration: `${DRAW_MS}ms` }}
                    fill="none"
                    strokeWidth={HALO_WIDTH}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />

                  <path
                    // The hook the tests select on. Tag names were the handle while this was a
                    // `<polyline>`; `<path>` is also what every icon in the shell is made of, so
                    // "the chart drew no line" needs a mark only the chart carries.
                    data-series={series.key}
                    className="chart-line"
                    d={series.solid}
                    // Normalises the curve's length to 1 so a single dash covers it whatever its
                    // real length — the draw-in needs no measurement. See `chart-draw` in the CSS.
                    pathLength={1}
                    style={{ animationDuration: `${DRAW_MS}ms`, stroke: series.color }}
                    fill="none"
                    strokeWidth={STROKE_WIDTH}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />

                  {series.tail ? (
                    <>
                      {/* A SOLID halo under a dashed line, so the dashes stay crisp where they
                          cross another series instead of showing it through the gaps. */}
                      <path
                        className="chart-tail chart-halo"
                        aria-hidden="true"
                        d={series.tail}
                        style={{
                          animationDuration: `${DOT_MS}ms`,
                          animationDelay: `${DRAW_MS}ms`,
                        }}
                        fill="none"
                        strokeWidth={HALO_WIDTH}
                        strokeLinecap="round"
                      />
                      <path
                        data-tail={series.key}
                        className="chart-tail"
                        d={series.tail}
                        /*
                         * Fades in where the pen arrives rather than drawing itself: the draw-in
                         * animates `stroke-dasharray`, which is the very property carrying the
                         * dash pattern here. One would overwrite the other.
                         */
                        style={{
                          animationDuration: `${DOT_MS}ms`,
                          animationDelay: `${DRAW_MS}ms`,
                          stroke: series.color,
                        }}
                        fill="none"
                        strokeWidth={STROKE_WIDTH}
                        strokeDasharray="4 4"
                        strokeLinecap="round"
                      />
                    </>
                  ) : null}

                  {series.points.map((p, index) => {
                    if (p.value === null) return null;
                    // The whole COLUMN swells, not one dot: the bubble is reporting every visible
                    // series at this bucket, so every one of them should be marked as read.
                    const inColumn = hovered?.index === index;
                    return (
                      <circle
                        key={`${series.key}-${index}`}
                        className="chart-dot"
                        cx={p.x}
                        cy={p.y}
                        r={inColumn ? DOT_R_HOVER : DOT_R}
                        strokeWidth="0.9"
                        style={{
                          fill: series.color,
                          // The bands below own every pointer event. A dot that could take one
                          // would be a 2px target competing with a 100px one.
                          pointerEvents: 'none',
                          animationDuration: `${DOT_MS}ms`,
                          /*
                           * Each dot waits for its own x, as a plain fraction of the draw. The pen
                           * eases OUT, so it is ahead of this linear schedule for most of the
                           * sweep and every dot lands just behind it — which is the read we want.
                           * A matching ease here would put the dots in front of the line.
                           */
                          animationDelay: `${
                            (index / Math.max(1, series.points.length - 1)) * DRAW_MS
                          }ms`,
                        }}
                      />
                    );
                  })}
                </g>
              );
            })}
          </g>

          {/*
            The hover bands: one full-height column per bucket, last in the SVG so they sit on top
            of everything and are the only things that take a pointer event.

            This replaced hovering the dots themselves, which were a 3px target — you had to find
            a dot to read a figure, and between dots the chart was inert. A column is ~100px wide
            at 12 buckets, so anywhere in the bucket works, and it can report all four series at
            once instead of whichever one you managed to hit.
          */}
          <g>
            {data.points.map((point, index) => {
              const centre = bucketX(index);
              const half = step > 0 ? step / 2 : width;
              // Clamped at the edges so the first and last bands are half-width rather than
              // hanging off the plot — the first and last points sit ON the edges.
              const x0 = Math.max(0, centre - half);
              const x1 = Math.min(width, centre + half);
              return (
                <rect
                  key={point.key}
                  className="chart-hit"
                  data-bucket={index}
                  x={x0}
                  y={0}
                  width={Math.max(0, x1 - x0)}
                  height={CHART_HEIGHT}
                  // Enter alone: the bands tile the plot edge to edge, so every pointer move
                  // that changes the answer crosses a boundary. `onMouseMove` would fire per
                  // pixel to re-derive the same bucket.
                  onMouseEnter={() => setHovered({ index })}
                />
              );
            })}
          </g>
        </svg>

        {hovered && hoveredPoint ? (
          /*
            Placed to the SIDE of the hovered column, flipping at the plot's midpoint.

            It used to sit above or below the point, which worked while the bubble was one series
            tall. Decomposing every series makes it ~190px in a 240px plot, so a vertical offset
            has nowhere to go — it would cover the column it is describing whichever way it went.
            Sideways it always leaves that column, its guide line and its dots in the clear.
          */
          <div
            style={{
              position: 'absolute',
              left:
                bucketX(hovered.index) < width / 2
                  ? bucketX(hovered.index) + TIP_GAP
                  : bucketX(hovered.index) - TIP_GAP,
              transform:
                bucketX(hovered.index) < width / 2 ? 'translateX(0)' : 'translateX(-100%)',
              /*
               * Load-bearing. An absolutely positioned box with `width: auto` is shrink-to-fit,
               * and its available space runs from `left` to the containing block's RIGHT edge —
               * `translateX(-100%)` moves the painted result but happens after layout, so it
               * never gives that space back. On the last buckets `left` is ~90% across the plot,
               * which left the bubble ~119px wide and collapsed every label onto its own figure.
               * `max-content` takes the width the sections actually need, wherever it is placed.
               */
              width: 'max-content',
              top: 2,
              transition: 'left 0.12s ease-out',
              pointerEvents: 'none',
              zIndex: 5,
            }}
          >
            <div
              role="tooltip"
              className="chart-tip"
              style={{
                background: color.tipBg,
                backdropFilter: 'blur(3px)',
                color: color.tipInkRow,
                border: `1px solid ${color.tipBorder}`,
                borderRadius: 9,
                boxShadow: '0 10px 28px -8px rgba(120,90,10,.25)',
                // Tighter than the table's bubble on purpose: this one carries four sections, and
                // the table's 10px/13px padding at 11.5px type would put it past the plot height.
                padding: '7px 9px',
              }}
            >
              {/*
                The label and the window are one header block with ONE hairline under it —
                whichever line is last carries the rule. Giving each its own border stacks two
                hairlines four pixels apart, which in a bubble this tight reads as a table.
              */}
              <div
                style={{
                  fontSize: fontSize.micro,
                  fontWeight: 700,
                  letterSpacing: '.3px',
                  color: color.tipInk,
                  ...(bucketWindow
                    ? { marginBottom: 1 }
                    : {
                        marginBottom: 5,
                        paddingBottom: 4,
                        borderBottom: `1px solid ${color.tipBorderInner}`,
                      }),
                }}
              >
                {hoveredPoint.label}
                {hoveredPoint.isPartial ? ' · PARTIAL' : ''}
              </div>

              {/*
                What the bucket actually covers (FR-087).

                A second line under the label rather than an extension of it: the label is the
                bucket's identity and stays the thing a reader matches against the x axis, while
                this is the window the figures below were summed over. Run together on one line
                (`Sep 26 · PARTIAL · Sep 1 – Sep 10`) the identity stops being findable, and the
                header row is the widest thing in a bubble that already has to fit a 240px plot.

                Weaker than the label and above the sections, so it reads as qualifying the
                bucket and not the first series' figures.

                It is the one place the dashed tail is explained in words: on a partial bucket
                the window ends at the sync, so `12:00 AM – 6:12 AM UTC` says why a daily flow
                point sits below the eleven beside it.
              */}
              {bucketWindow ? (
                <div
                  style={{
                    fontSize: fontSize.micro,
                    fontWeight: 500,
                    color: color.tipInkRow,
                    // Weaker than the label above it, so the header reads as one block with an
                    // emphasis rather than as two competing lines.
                    opacity: 0.85,
                    marginBottom: 5,
                    paddingBottom: 4,
                    borderBottom: `1px solid ${color.tipBorderInner}`,
                  }}
                >
                  {bucketWindow}
                </div>
              ) : null}

              {/*
                Two columns once there is more than one series, so four sections stack 2×2 and the
                bubble stays inside the plot's own height. One column would be ~350px tall against
                a 240px plot.
              */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: visible.length > 1 ? 'repeat(2, minmax(0, 1fr))' : '1fr',
                  columnGap: 14,
                  rowGap: 6,
                }}
              >
                {visible.map((series) => {
                  const point = series.points[hovered.index];
                  const value = point?.value ?? null;
                  return (
                    <div
                      key={series.key}
                      data-tip-series={series.key}
                      style={{ minWidth: 118 }}
                    >
                      {/* The section head: the series, its colour, and the figure the line is
                          drawn from. The rows under it decompose exactly this number. */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          fontSize: fontSize.micro,
                          fontWeight: 700,
                          color: color.tipInk,
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <span
                            aria-hidden="true"
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: series.color,
                            }}
                          />
                          {SERIES_LABEL[series.key]}
                        </span>
                        <span
                          className="mono"
                          style={{ fontFamily: font.mono, color: series.color }}
                        >
                          {formatValue(value)}
                        </span>
                      </div>

                      {rowsFor(series.key, hovered.index, value).map((r) => (
                        <div
                          key={r.label}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 8,
                            fontSize: '9.5px',
                            lineHeight: 1.45,
                            // Indented under the head, so four sections read as four groups
                            // rather than one twenty-row list.
                            paddingLeft: 10,
                          }}
                        >
                          <span>{r.label}</span>
                          <span
                            className="mono"
                            style={{ fontFamily: font.mono, fontWeight: 600, color: r.color }}
                          >
                            {r.value}
                          </span>
                        </div>
                      ))}

                      {/*
                        The lineage, at the density the bubble can afford (owner request
                        2026-09-10).

                        ⚠ **It is the SOURCE alone while more than one line is drawn, and the
                        full footer only when the legend has isolated one.** The plot is 240px
                        tall and the bubble has to sit inside it; four sections each carrying a
                        wrapped sentence is ~120px more than there is, and a bubble that
                        overflows its own plot is a worse answer than a shorter one. Four series
                        means four sources worth telling apart — TOA and OFFERS are Run Rate
                        where JOBS and SUBS are Nexus — and that fits on one 9px line.

                        The sentence is not lost: the same card's matrix view carries the whole
                        footer on every cell and on every row header (`<ChartTable>`), and the
                        table columns these series correspond to carry it in Region Performance.
                      */}
                      {provenanceFor(series.key) ? (
                        visible.length === 1 ? (
                          <ProvenanceFootnote provenance={provenanceFor(series.key)} />
                        ) : (
                          <div
                            style={{
                              marginTop: 3,
                              paddingLeft: 10,
                              fontSize: '9px',
                              fontWeight: 600,
                              letterSpacing: '.2px',
                              // The section's own row ink at full strength, not a dimmed
                              // variant: 9px is already the smallest type in the product, and
                              // fading it is how a line ends up below AA on the amber panel
                              // with nothing in `theme.contrast.test.ts` to catch it — that
                              // file reads `global.css`, and an inline `opacity` is invisible
                              // to it. The size and the label are what make it subordinate.
                              color: color.tipInk,
                            }}
                          >
                            {`Source: ${sourceFace(provenanceFor(series.key)!.sources)}`}
                          </div>
                        )
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/*
        X labels only. No Y-axis labels and no gridlines (FR-024, PRD §6.2).

        The padding used to be `0 ${PAD_X}px` — `PAD_X` in *viewBox units* applied as *CSS
        pixels*, two different units, so the labels drifted out of step with the points by
        however much the SVG happened to be scaled. Now that the drawing is 1:1 with the box
        there is nothing to convert: the row spans the same width the points do, and
        `space-between` puts the first and last labels flush under the first and last points.
      */}
      <div style={axisRowStyle}>
        {data.points.map((point, index) => {
          const isHovered = hovered?.index === index;
          const isPinned = index === summaryIndex;
          // The final bucket ends at as-of rather than a calendar boundary (PRD §8.4).
          const hint = point.isPartial
            ? `${point.label} — partial, ends at the as-of date`
            : point.label;
          const face = {
            fontFamily: font.mono,
            fontSize: '10px',
            // The hovered bucket names itself down here as well as in the bubble, so the guide
            // line lands on something rather than pointing into grey text. The picked one is
            // lit for as long as the strip is reporting it.
            color:
              isHovered || isPinned
                ? color.ink
                : point.isPartial
                  ? color.inkSubtle
                  : color.inkFaint,
            fontWeight: isHovered || isPinned ? 700 : 400,
          } as const;

          return (
            <button
              key={point.key}
              type="button"
              className="mono"
              data-bucket-label={index}
              aria-pressed={isPinned}
              // Says what the click does, not what the label reads — the visible text is already
              // the bucket's name, and "Jul 2026, button" leaves the effect to be guessed at.
              aria-label={`Summarise ${point.label}${
                point.isPartial ? ', a partial bucket,' : ''
              } below the chart`}
              title={hint}
              onClick={() => setPinned(index)}
              style={{
                ...face,
                background: 'none',
                /*
                 * No underline and no chrome of any kind (owner request 2026-09-03): the dark
                 * weight is the whole mark on this row, and the dotted guide up in the plot is
                 * what tells the pick apart from a label the pointer is merely crossing.
                 *
                 * The row cannot jiggle as the pick moves, because the labels are monospaced —
                 * `font.mono` gives 700 and 400 the same advance width, so the bolding costs no
                 * pixels and `space-between` keeps every label under its own point.
                 */
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                lineHeight: 1.2,
              }}
            >
              {point.label}
            </button>
          );
        })}
      </div>

      {/*
        One line, every visible series, at the bucket the labels above have picked (owner request
        2026-09-03). It opens on the latest bucket, which is the month the reader is in.

        Why a strip and not a second bubble: the bubble is a hover, so its figures exist only while
        the pointer is on the plot. This is the same twelve points read at rest — one bucket at a
        time, side by side, and still on screen while the reader is looking at the table below.

        Three things keep it honest, and all three are FR-034's subject:

        - **It names its own bucket, and both ends of its change.** `Aug 2026 · vs Jul 2026`. The
          change here is bucket-over-bucket and therefore moves with the *grain*, unlike every
          arrow in Region Performance, which is a fixed 30-day trailing window that never moves
          (FR-054). The two are on the same page; only the label separates them.
        - **It reads the legend.** `visible`, not `scaled` — isolate a line and the strip narrows
          with the plot, because a strip that reported four figures under one drawn line would be
          answering a question the chart is not.
        - **It is a read of `data`, never a second computation.** The face figure is the series'
          own value at this bucket, formatted exactly as the bubble formats it.
      */}
      {summaryPoint ? (
        <div
          data-bucket-summary={summaryPoint.key}
          /*
           * Polite, because the figures change under a button that stays focused: without it a
           * keyboard reader activates a label and is told nothing at all happened. Polite rather
           * than assertive — it is an answer to a click, not an alert.
           */
          aria-live="polite"
          style={{ ...summaryRowStyle, borderTop: `1px solid ${color.borderSubtle}` }}
        >
          <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span
              style={{
                fontSize: fontSize.captionStrong,
                fontWeight: 700,
                color: color.ink,
              }}
            >
              {summaryPoint.label}
              {summaryPoint.isPartial ? ' · partial' : ''}
            </span>
            <span style={{ fontSize: fontSize.caption, color: color.inkSubtle }}>
              {previousPoint ? `vs ${previousPoint.label}` : 'nothing earlier to compare'}
            </span>
          </span>

          {visible.map((series) => {
            const value = series.points[summaryIndex]?.value ?? null;
            // `jobs` is optional on the point, so this is `number | null | undefined` — which is
            // exactly what `bucketChange` refuses to compare.
            const previousValue = previousPoint ? previousPoint[series.key] : null;
            const change = bucketChange(value, previousValue);
            const face = change === null ? null : changeFace(change);

            return (
              <span
                key={series.key}
                data-summary-series={series.key}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {/*
                  The whole item is hidden from assistive technology and re-stated below in one
                  sentence. Read as separate nodes it comes out as "TOA 505 black up-pointing
                  triangle 3.8 percent", and the exact figure would be lost wherever the face is
                  compacted — the sentence carries every digit, as `<MetricCell>`'s name does.
                */}
                <span
                  aria-hidden="true"
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: series.color,
                    }}
                  />
                  <span
                    style={{
                      fontSize: fontSize.body,
                      fontWeight: 700,
                      color: color.inkRow,
                    }}
                  >
                    {SERIES_LABEL[series.key]}
                  </span>
                  <span
                    className="mono"
                    style={{
                      fontFamily: font.mono,
                      fontSize: fontSize.captionStrong,
                      fontWeight: 600,
                      color: value === null ? color.inkFaint : color.inkNumeric,
                    }}
                  >
                    {formatValue(value)}
                  </span>
                  {face ? (
                    <span
                      className="mono"
                      style={{
                        fontFamily: font.mono,
                        fontSize: fontSize.caption,
                        fontWeight: 700,
                        color: face.tone,
                      }}
                    >
                      {face.glyph}
                      {face.text}
                    </span>
                  ) : null}
                </span>

                <span className="sr-only">
                  {`${SERIES_FULL[series.key]}: ${formatValueExact(value)}`}
                  {face && previousPoint ? `, ${face.spoken} on ${previousPoint.label}` : ''}
                </span>
              </span>
            );
          })}
        </div>
      ) : null}

      {/* An accessible equivalent of the chart. FR-062's principle applied to a shape: the
          figures behind it must not be pointer-only. */}
      <table className="sr-only">
        <caption>{title} — 12 points, oldest to newest</caption>
        <thead>
          <tr>
            <th scope="col">Period</th>
            {/* The same series the chart drew — the accessible table must not claim a column
                the response never carried. */}
            {scaled.map((series) => (
              <th key={series.key} scope="col">
                {SERIES_FULL[series.key]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.points.map((point) => (
            <tr key={point.key}>
              <th scope="row">
                {point.label}
                {point.isPartial ? ' (partial)' : ''}
              </th>
              {scaled.map((series) => (
                <td key={series.key}>{point[series.key] ?? 'no data'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
